import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Box, F0Button, F0Heading, F0Text } from '@factorialco/f0-react'
import { Marketplace } from '@factorialco/f0-react/icons/app'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { NumberCounter } from 'modules/it_management/components/device_catalog/NumberCounter'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'

import type { DeviceRecommendation } from './types'

function getOnetimeOffer(recommendation: DeviceRecommendation) {
  return recommendation.pricing.offers.find((o) => o.type === 'onetime')
}

function getLowestRentingOffer(recommendation: DeviceRecommendation) {
  const renting = recommendation.pricing.offers.filter((o) => o.type === 'renting' && o.period)
  if (renting.length === 0) return undefined
  return renting.reduce((a, b) => ((a.period ?? 0) < (b.period ?? 0) ? a : b))
}

type RecommendationCardProps = {
  recommendation: DeviceRecommendation
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const cart = useDeviceCatalogCartContext()

  const [showQuantity, setShowQuantity] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const onetimeOffer = getOnetimeOffer(recommendation)
  const rentingOffer = getLowestRentingOffer(recommendation)

  const price = {
    amount: recommendation.pricing.amount,
    currency: recommendation.pricing.currency,
  }

  const monthlyPrice =
    rentingOffer?.period && rentingOffer.period > 0
      ? {
          amount: (rentingOffer.costWithoutVat ?? rentingOffer.cost) / rentingOffer.period,
          months: rentingOffer.period,
          currency: rentingOffer.currency,
        }
      : undefined

  const badge =
    recommendation.availability.deliveryConditions &&
    recommendation.availability.deliveryConditions.trim() !== ''
      ? { label: recommendation.availability.deliveryConditions }
      : undefined

  const handleViewDetails = () => {
    navigate({
      pathname: navigation.deviceCatalog.variantId.fullPath({ variantId: recommendation.id }),
      hash: '',
    })
  }

  const handleAddToCart = () => {
    if (!showQuantity) {
      setShowQuantity(true)
      return
    }

    if (!onetimeOffer) return

    cart.addItem({
      variantId: recommendation.id,
      productId: recommendation.id,
      name: recommendation.title,
      price: onetimeOffer.cost,
      currency: onetimeOffer.currency,
      image: recommendation.imageUrl,
      quantity,
      addons: [],
      offerId: onetimeOffer.id,
      offerType: 'onetime',
      offerPeriod: null,
    })

    setShowQuantity(false)
    setQuantity(1)
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <F0Box
        display="flex"
        flexDirection="column"
        gap="md"
        border="default"
        borderColor="secondary"
        borderRadius="lg"
        padding="lg"
        onClick={handleViewDetails}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleViewDetails()
          }
        }}
      >
        <F0Box width="full" height="48" display="flex" alignItems="center" justifyContent="center">
          <img
            src={recommendation.imageUrl}
            alt={recommendation.title}
            className="max-h-full max-w-full object-contain"
          />
        </F0Box>

        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading variant="heading" content={recommendation.title} />
          {badge && <F0Text variant="description" content={badge.label} />}
        </F0Box>

        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Text variant="label" content={formatPrice(price.amount, price.currency)} />
          {monthlyPrice && (
            <F0Text
              variant="description"
              content={i18n.t('it_management.device_catalog.detail.from_price_per_month', {
                amount: formatPrice(monthlyPrice.amount, monthlyPrice.currency),
              })}
            />
          )}
        </F0Box>
      </F0Box>

      {showQuantity ? (
        <F0Box display="flex" alignItems="center" gap="sm">
          <NumberCounter value={quantity} onChange={setQuantity} min={1} />
          <F0Button
            label={i18n.t('common.confirm')}
            onClick={handleAddToCart}
            size="sm"
            fullWidth
          />
          <F0Button
            label="×"
            onClick={() => {
              setShowQuantity(false)
              setQuantity(1)
            }}
            variant="outline"
            size="sm"
          />
        </F0Box>
      ) : (
        <F0Button
          label={i18n.t('it_management.device_catalog.detail.add_to_cart')}
          onClick={handleAddToCart}
          icon={Marketplace}
          size="sm"
          fullWidth
          disabled={!onetimeOffer}
        />
      )}
    </F0Box>
  )
}
